import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Translate } from 'react-localize-redux';
import Icon, { IconSizes, IconVariants } from '../../../components/foundations/Icon';
import { getTranslate } from '../../../selectors/locale';
import { download } from '../../../utils/export';
import { CHAT_EVENTS, trackChatEvent } from '../../constants';
import { fetchArtifactBlob } from '../../hooks/useArtifactContent';
import { formatFileSize } from '../../../utils/numbers';
import css from './FileArtifact.css';

const TRANSLATION_PREFIX = 'STATIC.PAGES.ANALYSIS_CHAT.GENERATED_FILE';

const PDF_MIME = 'application/pdf';

const iconForMime = mime => (mime === PDF_MIME ? 'file-text' : 'file-spreadsheet');

// `data` is inline during streaming; `metadata` carries mime/size on reload.
const FileArtifact = ({ artifact }) => {
    const [isDownloading, setIsDownloading] = useState(false);
    const [error, setError] = useState(false);
    const translate = useSelector(getTranslate);

    // Doubles as the download filename, so it has to be a plain string.
    const name = artifact.data?.name || artifact.title || translate(`${TRANSLATION_PREFIX}.DEFAULT_NAME`);
    const mime = artifact.data?.mime || artifact.metadata?.mime;
    const sizeBytes = artifact.data?.size_bytes ?? artifact.metadata?.size_bytes;

    const handleDownload = async () => {
        if (isDownloading || !artifact.content_url) return;
        setIsDownloading(true);
        setError(false);
        trackChatEvent(CHAT_EVENTS.DOWNLOAD_GENERATED_FILE, { fileName: name, mime });
        try {
            const blob = await fetchArtifactBlob(artifact.content_url);
            download(name, blob, mime);
        } catch (err) {
            setError(true);
        } finally {
            setIsDownloading(false);
        }
    };

    return (
        <div className={css.fileArtifact}>
            <div className={css.fileIcon}>
                <Icon name={iconForMime(mime)} size={IconSizes.MEDIUM} interactive={false} />
            </div>
            <div className={css.fileDetails}>
                <span className={css.fileName} title={name}>
                    {name}
                </span>
                <span className={css.fileMeta}>
                    {formatFileSize(sizeBytes)}
                    {error && (
                        <span className={css.fileError}>
                            {' '}
                            <Translate id={`${TRANSLATION_PREFIX}.DOWNLOAD_FAILED`} />
                        </span>
                    )}
                </span>
            </div>
            <button
                type="button"
                className={css.downloadButton}
                onClick={handleDownload}
                disabled={isDownloading || !artifact.content_url}
            >
                <Icon name="download" size={IconSizes.SMALL} variant={IconVariants.ACTIVE} interactive={false} />
                <Translate id={`${TRANSLATION_PREFIX}.DOWNLOAD`} />
            </button>
        </div>
    );
};

FileArtifact.propTypes = {
    artifact: PropTypes.shape({
        id: PropTypes.string,
        type: PropTypes.string,
        title: PropTypes.string,
        content_url: PropTypes.string,
        data: PropTypes.shape({
            file_id: PropTypes.string,
            name: PropTypes.string,
            mime: PropTypes.string,
            size_bytes: PropTypes.number,
        }),
        metadata: PropTypes.shape({
            mime: PropTypes.string,
            size_bytes: PropTypes.number,
        }),
    }).isRequired,
};

export default FileArtifact;
